import { useQuery } from "@tanstack/react-query";
import { useAxiosContext } from "../..";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import { ListFilterDto } from "../../services/base/base.dto";
import { Menu } from "primereact/menu";
import auditQuestionService from "../../services/audit-questions/audit-questions.service";
import { AuditQuestionDto } from "../../services/audit-questions/audit-questions.dto";
import { MenuItem } from "primereact/menuitem";
import deleteConfirmation from "../../components/confirmations/DeleteConfirmation";
import ArfDataTable, { ColumnMeta } from "../../components/table/ArfDataTable";
import AuditQuestionModal from "./AuditQuestionModal";
import { Button } from "primereact/button";
import i18n from "../../utils/i18n";
import { Tooltip } from "primereact/tooltip";

const AuditQuestion = () => {
  const { t } = useTranslation();
  const toaster = useAxiosContext();

  const [selectedItem, setSelectedItem] = useState<AuditQuestionDto>();
  const [visibleDialog, setVisibleDialog] = useState<boolean>(false);
  const menu = useRef<Menu>(null);

  const [filter, setFilter] = useState<ListFilterDto>();
  const queryKey = ["AuditQuestions", filter];

  const {
    data: auditQuestionsResponse,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKey,
    queryFn: () =>
      auditQuestionService.GetAll({
        params: filter,
      }),
    enabled: filter?.pageSize !== undefined,
  });

  var actions: MenuItem[] = [
    {
      label: t("Edit"),
      icon: "pi pi-pencil",
      command: () => {
        if (selectedItem?.id) {
          setVisibleDialog(true);
        }
      },
    },
    {
      label: t("Delete"),
      icon: "pi pi-times",
      command: () => {
        deleteConfirmation(t, {
          message: t("DeleteMessage"),
          header: "",
          accept: () => {
            if (selectedItem)
              auditQuestionService.Delete(selectedItem.id).then(response => {
                if (!response.error) {
                  toaster.showMessage(t("Deleted"), "success");
                  refetch();
                }
              });
            setSelectedItem(undefined);
          },
          reject: () => {
            setSelectedItem(undefined);
          },
        });
      },
    },
  ];

  const columns: ColumnMeta[] = [
    {
      field:
        i18n.language === "tr" ? "questionTR" : i18n.language === "zh" ? "questionCH" : "question",
      header: t("Question"),
      sortable: true,
      filterable: true,
      body: (row: AuditQuestionDto) => {
        let question =
          i18n.language === "tr"
            ? row.questionTR
            : i18n.language === "zh"
            ? row.questionCH
            : row.question;
        return (
          <>
            <span className={`tooltip-question-${row.id}`}>
              {question.length > 103 ? `${question.slice(0, 103)}... ` : question}
            </span>
            {row.question.length > 103 && (
              <Tooltip
                target={`.tooltip-question-${row.id}`}
                position="top"
                content={question}
                className={"language-tooltip w-6 sm:w-4 lg:w-2"}
              />
            )}
          </>
        );
      },
    },
    {
      field: "sector.name",
      header: t("Sector"),
      sortable: true,
      filterable: true,
    },
    {
      field: "standard.name",
      header: t("Standard"),
      sortable: true,
      filterable: true,
    },
    {
      field: "actions",
      header: "Actions",
      body: (row: AuditQuestionDto) => {
        return (
          <>
            <Button
              icon="pi pi-ellipsis-h"
              rounded
              text
              onClick={e => {
                setSelectedItem(row);
                menu?.current?.toggle(e);
              }}
            />
          </>
        );
      },
      style: {
        width: "3rem",
      },
    },
  ];

  return (
    <div className="custom-datatable px-6 py-3">
      <Menu model={actions} popup ref={menu} />
      <ArfDataTable
        data={auditQuestionsResponse?.result}
        loading={isLoading}
        columns={columns}
        filterChange={filter => {
          setFilter(filter);
        }}
        actionOptions={{
          text: t("AddAuditQuestion"),
          command() {
            setVisibleDialog(true);
            setSelectedItem(undefined);
          },
          icon: "",
        }}
        exportOptions={{
          enabled: false,
        }}
        emptyMessageOptions={{
          buttonText: t("AddAuditQuestion"),
          text: t("EmptyAuditQuestionTableMessage"),
        }}
      />
      {visibleDialog && (
        <AuditQuestionModal
          visible={visibleDialog}
          onHide={() => {
            setVisibleDialog(false);
            setSelectedItem(undefined);
            refetch();
          }}
          selectedAuditQuestion={selectedItem}
        />
      )}
    </div>
  );
};

export default AuditQuestion;
